import BaseController from "./BaseController";
import Fragment from "sap/ui/core/Fragment";
import JSONModel from "sap/ui/model/json/JSONModel";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import type Dialog from "sap/m/Dialog";
import type { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import type { Switch$ChangeEvent } from "sap/m/Switch";
import type { ResponseError } from "../types/Errors";
import { PERMISSION_CATALOG } from "../auth/PermissionCatalog";
import { problemCode, problemMessage } from "../util/ProblemDetails";

/** How long loadOverrides waits for the element binding to resolve, and how many times. */
const LOAD_OVERRIDES_RETRY_MS = 400;
const LOAD_OVERRIDES_MAX_ATTEMPTS = 10;

interface PermissionDisplayRow {
	prefix: "grant" | "revoke";
	permission: string;
	icon: string;
}

interface PermissionEditRow {
	code: string;
	label: string;
	state: "inherit" | "grant" | "deny";
}

/**
 * Row w sekcji "License assignments". Wszystkie assignmenty są z definicji InPortal —
 * <c>AssignUserToLicenseHandler</c> wymaga <c>Product.LicensingMode == InPortal</c>.
 */
interface AssignmentRow {
	assignmentId: string;
	licenseId: string;
	productId: string;
	productCode: string;
	productName: string;
	deploymentId: string;
	deploymentName: string;
	assignedAt: string;
}

interface ViewState {
	userId: string;
	overrides: PermissionDisplayRow[];
	assignments: AssignmentRow[];
	permissionRows: PermissionEditRow[];
	changeRole: { role: string };
	promote: { role: string };
}

interface ServiceRoleResponseRow {
	name: string;
	service: string;
	environment: "Production" | "Development";
	granted: boolean;
	revokesClientOfflineAccess: boolean;
}

interface ServiceRolesResponse {
	eligible: boolean;
	ineligibleReason: string | null;
	roles: ServiceRoleResponseRow[];
}

/** One switch in the "Service access" section; label and group are resolved from i18n here. */
interface ServiceRoleRow extends ServiceRoleResponseRow {
	label: string;
	group: string;
}

interface ServiceRolesState {
	eligible: boolean;
	roles: ServiceRoleRow[];
}

/**
 * User Detail (Object Page) — wzorzec OrganizationDetail. Bind do
 * <c>/Users(&lt;guid&gt;)</c> przez bindElement, sekcje General / Membership / Permissions.
 *
 * Actions on the page: Deactivate / Reactivate / Edit overrides / Change role / Promote / Demote.
 *
 * @namespace computec.b2b.portal.controller
 */
export default class PortalUserDetail extends BaseController {
	public onInit(): void {
		const initial: ViewState = {
			userId: "",
			overrides: [],
			assignments: [],
			permissionRows: [],
			changeRole: { role: "" },
			promote: { role: "" }
		};
		this.getView().setModel(new JSONModel(initial), "view");
		const serviceRoles: ServiceRolesState = { eligible: false, roles: [] };
		this.getView().setModel(new JSONModel(serviceRoles), "serviceRoles");

		this.getRouter().getRoute("portalUserDetail")?.attachPatternMatched((event) => {
			void this.onPatternMatched(event);
		});
	}

	private async onPatternMatched(event: Route$PatternMatchedEvent): Promise<void> {
		const args = event.getParameter("arguments") as { id?: string } | undefined;
		const id = args?.id ?? "";

		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/userId", id);

		// bindElement: pojedyncza encja po Id; OData v4 Edm.Guid literal w nawiasach bez apostrofów.
		// $select names OrganizationId explicitly: with autoExpandSelect the model asks only for the
		// properties the XML view binds, and this one is read from code alone (loadOverrides), so it
		// never reached the request - getProperty then answered "invalid segment" forever.
		this.getView().bindElement({
			path: `/Users(${id})`,
			model: "odata",
			parameters: { $select: "OrganizationId" }
		});

		const bundle = await this.getResourceBundle();
		this.setPageHeader({
			title: bundle.getText("userDetail.title") ?? "Portal user",
			subtitle: ""
		});

		await Promise.all([
			this.loadOverrides(id),
			this.loadAssignments(id),
			this.loadServiceRoles(id),
		]);
	}

	/**
	 * Fetchuje aktywne <c>LicenseAssignment</c> usera z denormalizowanego OData feed'u
	 * (<c>/odata/v4/LicenseAssignments</c>) + sortuje po ProductCode → DeploymentName.
	 *
	 * Raw $filter (a nie UI5 <c>Filter</c> obiekt) — patrz gotcha "Custom .NET enum types" w sapui5-b2b:
	 * <c>Status</c> jest custom <c>LicenseAssignmentStatus</c> enum, UI5 formatLiteral by failnął.
	 * Edm.Guid literal: bez apostrofów.
	 */
	private async loadAssignments(userId: string): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/assignments", []);
		if (!userId) {
			return;
		}
		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const url = `/odata/v4/LicenseAssignments`
				+ `?$filter=AssignedUserId eq ${userId} and Status eq 'Active'`
				+ `&$orderby=ProductCode,DeploymentName`
				+ `&$select=Id,LicenseId,AssignedAt,ProductId,ProductCode,ProductName,DeploymentId,DeploymentName`;
			const resp = await fetcher.fetch(url, { headers: { Accept: "application/json" } });
			if (!resp.ok) {
				return;
			}
			const body = await resp.json() as { value?: AssignmentODataItem[] };
			const rows: AssignmentRow[] = (body.value ?? []).map(a => ({
				assignmentId: a.Id,
				licenseId: a.LicenseId,
				productId: a.ProductId,
				productCode: a.ProductCode,
				productName: a.ProductName,
				deploymentId: a.DeploymentId,
				deploymentName: a.DeploymentName,
				assignedAt: a.AssignedAt,
			}));
			view.setProperty("/assignments", rows);
		} catch {
			// Best-effort — sekcja pokaże empty state.
		}
	}

	/**
	 * Pobiera overrides usera przez REST endpoint <c>GET /api/v1/organizations/{orgId}/users</c>
	 * (OData /Users nie eksponuje kolekcji PermissionOverrides — kompromis z step 1).
	 * Filtruje po userId klient-side. Wymaga znania orgId — wyciągamy z bindContextu po
	 * tym jak bindElement zaresolves, więc wołamy też ponownie po requestCompleted.
	 */
	private async loadOverrides(userId: string, attempt = 0): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		view.setProperty("/overrides", []);
		view.setProperty("/permissionOverridesRaw", []);

		// Próbujemy fetchnąć orgId z bindContextu — przy pierwszym wejściu może jeszcze nie być,
		// bo bindElement jest async. W takim razie czekamy aż odata model będzie miał kontekst.
		const ctx = this.getView().getBindingContext("odata");
		const orgId = (ctx?.getProperty("OrganizationId") as string | undefined) ?? "";
		if (!orgId) {
			// Retry after binding resolves — krótki delay (UI5 odata v4 doesn't expose ready promise easily).
			// BOUNDED: the wait is for a binding that resolves in well under a second. An uncapped retry
			// turns a property that can never arrive into a permanent 400 ms loop - which is exactly what
			// a missing $select did here - so give up loudly instead of flooding the console.
			if (attempt >= LOAD_OVERRIDES_MAX_ATTEMPTS) {
				console.warn(
					`[Portal] permission overrides not loaded: OrganizationId never arrived on the user ` +
					`binding after ${LOAD_OVERRIDES_MAX_ATTEMPTS} attempts - check the binding's $select.`
				);
				return;
			}
			setTimeout(() => { void this.loadOverrides(userId, attempt + 1); }, LOAD_OVERRIDES_RETRY_MS);
			return;
		}

		const fetcher = this.getOwnerComponent().getAuthFetch();
		try {
			const items = await fetcher.json<PortalUserListItem[]>(`/api/v1/organizations/${orgId}/users`);
			const me = items.find(u => u.id === userId);
			if (!me) {
				return;
			}
			view.setProperty("/permissionOverridesRaw", me.permissionOverrides);
			view.setProperty("/overrides", me.permissionOverrides.map((ov): PermissionDisplayRow => {
				const prefix: "grant" | "revoke" = ov.startsWith("+") ? "grant" : "revoke";
				return {
					prefix,
					permission: ov.substring(1),
					icon: prefix === "grant" ? "sap-icon://add" : "sap-icon://decline"
				};
			}));
		} catch {
			// Best-effort — sekcja Permissions pokaże empty state.
		}
	}

	public onNavBack(): void {
		this.navTo("portalUsers");
	}

	public onRefresh(): void {
		// bindElement to context binding — najprościej refresh przez sam ODataModel
		// (analogicznie do OrganizationDetail.onSaveProfile).
		this.getView().getModel("odata")?.refresh();
		const userId = (this.getView().getModel("view") as JSONModel).getProperty("/userId") as string;
		void this.loadAssignments(userId);
	}

	public onOpenDeploymentFromAssignment(event: {
		getSource(): { getBindingContext(name: string): { getProperty(p: string): unknown } | null };
	}): void {
		const id = event.getSource().getBindingContext("view")?.getProperty("deploymentId") as string | undefined;
		if (id) {
			this.navTo("deploymentDetail", { id });
		}
	}

	public onOpenOrganization(): void {
		const orgId = this.getView().getBindingContext("odata")?.getProperty("OrganizationId") as string | undefined;
		if (orgId) {
			this.navToOrganization(orgId);
		}
	}

	public onOpenPartner(): void {
		const id = this.getView().getBindingContext("odata")?.getProperty("PartnerOrgId") as string | undefined;
		if (id) {
			this.navToOrganization(id);
		}
	}

	public onOpenCustomer(): void {
		const id = this.getView().getBindingContext("odata")?.getProperty("CustomerOrgId") as string | undefined;
		if (id) {
			this.navToOrganization(id);
		}
	}

	// === Deactivate / Reactivate ===

	public async onDeactivate(): Promise<void> {
		const bundle = await this.getResourceBundle();
		MessageBox.confirm(
			bundle.getText("userDetail.confirm.deactivate.message") ?? "Are you sure?",
			{
				title: bundle.getText("userDetail.confirm.deactivate.title") ?? "Deactivate user",
				onClose: (action: string) => {
					// MessageBox.Action.OK = "OK" — porównujemy do stringa, żeby uniknąć
					// @typescript-eslint/no-unsafe-enum-comparison.
					if (action === "OK") {
						void this.callLifecycle("deactivate", "userDetail.deactivate.success", "userDetail.deactivate.failure");
					}
				}
			}
		);
	}

	public async onReactivate(): Promise<void> {
		await this.callLifecycle("reactivate", "userDetail.reactivate.success", "userDetail.reactivate.failure");
	}

	private async callLifecycle(action: "deactivate" | "reactivate", successKey: string, failureKey: string): Promise<void> {
		const bundle = await this.getResourceBundle();
		const userId = (this.getView().getModel("view") as JSONModel).getProperty("/userId") as string;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/users/${userId}/${action}`, { method: "POST" });
			MessageToast.show(bundle.getText(successKey) ?? "Done.");
			this.getView().getModel("odata")?.refresh();
			// ADR-017 §4: deactivate kaskaduje release LicenseAssignmentów — przeładuj sekcję.
			await this.loadAssignments(userId);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText(failureKey) ?? "Operation failed."));
		}
	}

	// === Change role section (ADR-017 §7) ===

	public async onChangeRoleSubmit(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const userId = view.getProperty("/userId") as string;
		const newRole = view.getProperty("/changeRole/role") as string;

		if (!newRole) {
			return;
		}

		const title = bundle.getText("userDetail.changeRole.confirmDialogTitle") ?? "Confirm role change";
		const message = `${title}: ${newRole}`;

		// Use MessageBox.confirm jako async-w-callback — ujmujemy w Promise (analogicznie do onDeactivate).
		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(message, {
				title,
				onClose: (action: string) => resolve(action === "OK")
			});
		});
		if (!confirmed) {
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/users/${userId}/role`, {
				method: "POST",
				body: JSON.stringify({ NewRole: newRole })
			});
			MessageToast.show(bundle.getText("userDetail.changeRole.success") ?? "Role changed.");
			view.setProperty("/changeRole", { role: "" });
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			// The contract carries the code in its own member (spec 29 § D-031), so these branches
			// compare it rather than scanning the body for a substring.
			const code = problemCode(err.bodyText ?? "");
			if (code === "user.role_only_in_computec") {
				MessageBox.error(bundle.getText("userDetail.changeRole.errorRoleOnlyInComputec") ?? "This role is only available for CompuTec users.");
			} else if (code === "user.role_change_requires_active") {
				MessageBox.error(bundle.getText("userDetail.changeRole.errorRequiresActive") ?? "User must be Active.");
			} else if (code === "grant.self_target") {
				// Grant ceiling: the two codes below say something about the caller, not about the
				// target, so the generic message would point the admin at the wrong account.
				MessageBox.error(bundle.getText("userDetail.changeRole.errorSelfTarget") ?? "You cannot change your own role.");
			} else if (code === "grant.exceeds_caller_permissions") {
				MessageBox.error(bundle.getText("userDetail.changeRole.errorExceedsCaller") ?? "You cannot grant a role you do not hold, or change an account whose role carries permissions you do not hold.");
			} else {
				MessageBox.error(problemMessage(err.bodyText ?? "", bundle.getText("userDetail.changeRole.errorGeneric") ?? "Failed to change role."));
			}
		}
	}

	// === Service access section (card 43, spec 29 D-122) ===

	/**
	 * GET /api/v1/users/{id}/service-roles. Only callers holding service-roles.manage ask: without it
	 * the endpoint answers 403, and the section is hidden by its own visible binding anyway.
	 */
	private async loadServiceRoles(userId: string): Promise<void> {
		const model = this.getView().getModel("serviceRoles") as JSONModel;
		model.setData({ eligible: false, roles: [] } satisfies ServiceRolesState);
		if (!userId || !this.getOwnerComponent().getPermissions().has("service-roles.manage")) {
			return;
		}

		const bundle = await this.getResourceBundle();
		try {
			const body = await this.getOwnerComponent().getAuthFetch()
				.json<ServiceRolesResponse>(`/api/v1/users/${userId}/service-roles`);
			const roles: ServiceRoleRow[] = body.roles.map((role) => ({
				...role,
				label: `${bundle.getText(`serviceRoles.role.${role.name}`) ?? role.name} (${role.name})`,
				group: `${bundle.getText(`serviceRoles.service.${role.service}`) ?? role.service} · `
					+ `${bundle.getText(`serviceRoles.environment.${role.environment}`) ?? role.environment}`
			}));
			model.setData({ eligible: body.eligible, roles } satisfies ServiceRolesState);
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText ?? "", bundle.getText("serviceRoles.error.load") ?? "Could not load service roles."));
		}
	}

	public async onServiceRoleSwitch(event: Switch$ChangeEvent): Promise<void> {
		const source = event.getSource();
		const context = source.getBindingContext("serviceRoles");
		const row = context?.getObject() as ServiceRoleRow | undefined;
		if (!context || !row) {
			return;
		}

		const grant = event.getParameter("state") === true;
		const bundle = await this.getResourceBundle();
		const userId = (this.getView().getModel("view") as JSONModel).getProperty("/userId") as string;
		const messageKey = grant
			? "serviceRoles.confirm.grant"
			: row.revokesClientOfflineAccess ? "serviceRoles.confirm.revokeOfflineAccess" : "serviceRoles.confirm.revoke";
		const title = bundle.getText(grant ? "serviceRoles.confirm.grantTitle" : "serviceRoles.confirm.revokeTitle") ?? "";

		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(bundle.getText(messageKey, [row.name]) ?? row.name, {
				title,
				onClose: (action: string) => resolve(action === "OK")
			});
		});
		if (!confirmed) {
			// The two-way binding already flipped the switch; put it back.
			(this.getView().getModel("serviceRoles") as JSONModel).setProperty(`${context.getPath()}/granted`, !grant);
			return;
		}

		try {
			await this.getOwnerComponent().getAuthFetch().json(
				`/api/v1/users/${userId}/service-roles/${encodeURIComponent(row.name)}`,
				{ method: grant ? "PUT" : "DELETE" });
			MessageToast.show(bundle.getText(grant ? "serviceRoles.success.granted" : "serviceRoles.success.revoked") ?? "");
		} catch (e) {
			const err = e as ResponseError;
			const errorKeys: Record<string, string> = {
				"grant.self_target": "serviceRoles.error.selfTarget",
				"service_role.recipient_not_staff": "serviceRoles.error.notStaff",
				"service_role.recipient_inactive": "serviceRoles.error.inactive",
				"service_role.not_found": "serviceRoles.error.notFound",
				"user.keycloak_provisioning_failed": "serviceRoles.error.keycloak"
			};
			const key = errorKeys[problemCode(err.bodyText ?? "")];
			MessageBox.error(key
				? bundle.getText(key) ?? key
				: problemMessage(err.bodyText ?? "", bundle.getText("serviceRoles.error.generic") ?? "Could not change the service role."));
		}

		// Keycloak is the source of truth: reload instead of trusting the switch state.
		await this.loadServiceRoles(userId);
	}

	// === Reinvite (ADR-017 §6) ===

	/**
	 * POST /api/v1/users/{id}/reinvite — generuje nowy token zaproszenia i każe Keycloakowi wysłać
	 * świeży invitation email. Sekcja widoczna tylko dla Pending + permission portal-users.invite.
	 */
	public async onReinviteSubmit(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const userId = (this.getView().getModel("view") as JSONModel).getProperty("/userId") as string;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/users/${userId}/reinvite`, { method: "POST" });
			MessageToast.show(bundle.getText("userDetail.reinvite.success") ?? "Invitation resent.");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("userDetail.reinvite.error") ?? "Could not resend the invitation."));
		}
	}

	// === Permission overrides dialog (reuses EditUserPermissionsDialog fragment) ===

	private editPermissionsDialog?: Dialog;

	public async onEditOverrides(): Promise<void> {
		const view = this.getView().getModel("view") as JSONModel;
		const existing = (view.getProperty("/permissionOverridesRaw") as string[] | null) ?? [];
		const bundle = await this.getResourceBundle();

		view.setProperty("/permissionRows", PERMISSION_CATALOG.map((code): PermissionEditRow => {
			const label = bundle.getText(`permission.${code}`) || code;
			if (existing.includes(`+${code}`)) {
				return { code, label, state: "grant" };
			}
			if (existing.includes(`-${code}`)) {
				return { code, label, state: "deny" };
			}
			return { code, label, state: "inherit" };
		}));

		if (!this.editPermissionsDialog) {
			this.editPermissionsDialog = await Fragment.load({
				id: this.getView().getId(),
				name: "computec.b2b.portal.view.EditUserPermissionsDialog",
				controller: this
			}) as Dialog;
			this.getView().addDependent(this.editPermissionsDialog);
		}
		this.editPermissionsDialog.open();
	}

	public onCancelPermissions(): void {
		this.editPermissionsDialog?.close();
	}

	public formatPermissionStateIndex(state: string | null | undefined): number {
		switch (state) {
			case "grant": return 1;
			case "deny": return 2;
			default: return 0;
		}
	}

	public onPermissionStateChange(event: {
		getSource(): {
			getBindingContext(name: string): { getPath(): string } | null;
			getSelectedIndex(): number;
		};
	}): void {
		const source = event.getSource();
		const ctx = source.getBindingContext("view");
		if (!ctx) {
			return;
		}
		const idx = source.getSelectedIndex();
		const newState: PermissionEditRow["state"] = idx === 1 ? "grant" : idx === 2 ? "deny" : "inherit";
		(this.getView().getModel("view") as JSONModel).setProperty(`${ctx.getPath()}/state`, newState);
	}

	public async onSavePermissions(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const userId = view.getProperty("/userId") as string;
		const rows = (view.getProperty("/permissionRows") as PermissionEditRow[]) ?? [];

		const overrides = rows
			.filter(r => r.state !== "inherit")
			.map(r => (r.state === "grant" ? `+${r.code}` : `-${r.code}`));

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/users/${userId}/permission-overrides`, {
				method: "PUT",
				body: JSON.stringify({ overrides })
			});
			MessageToast.show(bundle.getText("userDetail.overrides.success") ?? "Overrides saved.");
			this.editPermissionsDialog?.close();
			await this.loadOverrides(userId);
		} catch (e) {
			const err = e as ResponseError;
			const code = problemCode(err.bodyText ?? "");
			// Grant ceiling: the backend refuses a grant the caller does not hold, and refuses any
			// edit aimed at the caller's own account. A generic message says nothing here, so we
			// branch on the two codes.
			if (code === "grant.exceeds_caller_permissions") {
				MessageBox.error(bundle.getText("userDetail.overrides.errorExceedsCaller") ?? "You cannot grant a permission you do not hold, or change an account whose role carries permissions you do not hold.");
			} else if (code === "grant.self_target") {
				MessageBox.error(bundle.getText("userDetail.overrides.errorSelfTarget") ?? "You cannot edit your own permissions.");
			} else {
				MessageBox.error(problemMessage(err.bodyText, bundle.getText("userDetail.overrides.failure") ?? "Could not save overrides."));
			}
		}
	}

	// === Promote license-only → portal user (ADR-018 §6) ===

	public async onPromoteSubmit(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const view = this.getView().getModel("view") as JSONModel;
		const userId = view.getProperty("/userId") as string;
		const newRole = view.getProperty("/promote/role") as string;

		if (!newRole) {
			return;
		}

		const title = bundle.getText("userDetail.promote.confirmDialogTitle") ?? "Confirm promotion";
		const message = `${title}: ${newRole}`;

		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(message, {
				title,
				onClose: (action: string) => resolve(action === "OK")
			});
		});
		if (!confirmed) {
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/users/${userId}/promote-to-portal`, {
				method: "POST",
				body: JSON.stringify({ NewRole: newRole })
			});
			MessageToast.show(bundle.getText("userDetail.promote.success") ?? "User promoted.");
			view.setProperty("/promote", { role: "" });
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			const code = problemCode(err.bodyText ?? "");
			// Grant ceiling - same two codes as the change-role section, same reason for branching.
			if (code === "grant.self_target") {
				MessageBox.error(bundle.getText("userDetail.promote.errorSelfTarget") ?? "You cannot promote your own account.");
			} else if (code === "grant.exceeds_caller_permissions") {
				MessageBox.error(bundle.getText("userDetail.promote.errorExceedsCaller") ?? "You cannot grant a role that carries permissions you do not have yourself.");
			} else {
				MessageBox.error(problemMessage(err.bodyText, bundle.getText("userDetail.promote.errorGeneric") ?? "Promote failed."));
			}
		}
	}

	// === Demote portal user → license-only (ADR-018 §6) ===

	public async onDemoteSubmit(): Promise<void> {
		const bundle = await this.getResourceBundle();
		const userId = (this.getView().getModel("view") as JSONModel).getProperty("/userId") as string;

		const title = bundle.getText("userDetail.demote.confirmDialogTitle") ?? "Confirm demotion";
		const warning = bundle.getText("userDetail.demote.warning") ?? "User will lose portal access.";
		const message = `${title}\n\n${warning}`;

		const confirmed = await new Promise<boolean>((resolve) => {
			MessageBox.confirm(message, {
				title,
				onClose: (action: string) => resolve(action === "OK")
			});
		});
		if (!confirmed) {
			return;
		}

		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			await fetcher.json(`/api/v1/users/${userId}/demote-to-license-only`, { method: "POST" });
			MessageToast.show(bundle.getText("userDetail.demote.success") ?? "User demoted.");
			this.getView().getModel("odata")?.refresh();
		} catch (e) {
			const err = e as ResponseError;
			MessageBox.error(problemMessage(err.bodyText, bundle.getText("userDetail.demote.errorGeneric") ?? "Demote failed."));
		}
	}
}

interface PortalUserListItem {
	id: string;
	email: string;
	firstName: string;
	lastName: string;
	role: string | null;
	status: string;
	permissionOverrides: string[];
}

interface AssignmentODataItem {
	Id: string;
	LicenseId: string;
	AssignedAt: string;
	ProductId: string;
	ProductCode: string;
	ProductName: string;
	DeploymentId: string;
	DeploymentName: string;
}

